import { useEffect, useState } from "react";
import { Link } from "wouter";
import { MapPin } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import { collection, getDocs } from "firebase/firestore";
import db from "../lib/firebaseDb";

interface Region {
  id: string;
  name: string;
  description: string;
  imageUrl: string;
  mapLocation: {
    latitude: number;
    longitude: number;
  };
  highlights: string[];
}

interface MapLocation {
  name: string;
  top?: string;
  left?: string;
  right?: string;
  bottom?: string;
}

export default function ExploreMap() {
  const [regions, setRegions] = useState<Region[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchRegions = async () => {
      try {
        setIsLoading(true);
        const querySnapshot = await getDocs(collection(db, "regions"));
        const regionsData = querySnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        })) as Region[];
        
        // Remove duplicates by ensuring each region name is unique
        const uniqueRegions = Array.from(
          new Map(regionsData.map(region => [region.name, region])).values()
        );
        
        setRegions(uniqueRegions);
      } catch (err) {
        console.error("Error fetching regions:", err);
        setError("Failed to load map data");
      } finally {
        setIsLoading(false);
      }
    };

    fetchRegions();
  }, []);

  // // Key locations on the map from regions data
  // const mapLocations: MapLocation[] = [
  //   { name: "Istanbul", top: "22%", left: "20%" },
  //   { name: "Cappadocia", top: "48%", left: "57%" },
  //   { name: "Antalya", bottom: "30%", left: "43%" }
  // ];

  if (error) {
    return (
      <section className="py-12 bg-white">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl md:text-4xl font-sans font-bold text-[#333333] mb-2 text-center">Explore Turkey</h2>
          <p className="text-center text-red-500 mb-8">Failed to load map data. Please try again later.</p>
        </div>
      </section>
    );
  }

  return (
    <section className="py-12 bg-white">
      <div className="container mx-auto px-4 max-w-4xl">
        <h2 className="text-3xl md:text-4xl font-sans font-bold text-[#333333] mb-2 text-center">Explore Turkey</h2>
        <p className="text-center text-gray-600 mb-8 max-w-3xl mx-auto">
          Navigate through the diverse regions of Turkey and discover the unique attractions each has to offer.
        </p>
        
        <div className="bg-[#F5F5F5] p-3 md:p-4 rounded-lg shadow-md">
          <div className="relative aspect-[16/9] bg-white rounded-lg overflow-hidden">
            {/* Interactive Google Map */}
            <iframe 
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d6379676.998281084!2d30.049498973731542!3d39.00223753443498!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x14b0155c964f2671%3A0x40d9dbd42a625f2a!2sTurkey!5e0!3m2!1sen!2sus!4v1650306897137!5m2!1sen!2sus"
              width="100%" 
              height="100%" 
              style={{ border: 0 }} 
              allowFullScreen 
              loading="lazy" 
              referrerPolicy="no-referrer-when-downgrade"
              title="Map of Turkey"
              className="w-full h-full"
            ></iframe>
            
            {/* Map overlay with markers
            <div className="absolute inset-0 p-4 md:p-6 pointer-events-none">
              {mapLocations.map((location, index) => (
                <div 
                  key={index}
                  className="absolute group pointer-events-auto"
                  style={{
                    top: location.top || "auto",
                    left: location.left || "auto",
                    right: location.right || "auto",
                    bottom: location.bottom || "auto",
                  }}
                >
                  <div className="w-4 h-4 bg-[#E30A17] rounded-full animate-pulse-slow"></div>
                  <div className="hidden group-hover:block absolute top-full left-1/2 transform -translate-x-1/2 mt-2 bg-white p-2 rounded shadow-md z-10 w-32">
                    <p className="text-sm font-sans font-semibold text-center">{location.name}</p>
                  </div>
                </div>
              ))}
            </div> */}
          </div>
          
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-7 gap-1 md:gap-2 mt-3 text-xs md:text-sm">
            {isLoading ? (
              // Skeleton loading state
              Array(7).fill(0).map((_, index) => (
                <Skeleton key={index} className="h-10 w-full" />
              ))
            ) : (
              // Region buttons - each region only appears once
              regions.map((region) => (
                <Link key={region.id} href={`/destinations#region-${region.id}`}>
                  <div className="bg-white p-2 rounded text-center hover:bg-[#30D5C8] hover:text-white transition duration-200 cursor-pointer">
                    <span className="font-sans font-medium">{region.name}</span>
                  </div>
                </Link>
              ))
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
